import React from "react";
import { useStickyTableHeader } from "../../hooks/useStickyTableHeader";

const extractText = (node) => {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (React.isValidElement(node)) return extractText(node.props.children);
  return "";
};

const checkHasActionColumn = (thead) => {
  if (!thead || !React.isValidElement(thead)) return false;
  const trList = React.Children.toArray(thead.props.children);
  const lastTr = trList[trList.length - 1];
  if (!lastTr || !React.isValidElement(lastTr)) return false;
  const thList = React.Children.toArray(lastTr.props.children);
  if (!thList.length) return false;
  const lastTh = thList[thList.length - 1];
  if (!lastTh || !React.isValidElement(lastTh)) return false;
  const thText = extractText(lastTh).trim().toLowerCase();
  return thText === "action" || thText === "actions" || thText.includes("action");
};

const parseTableElement = (children) => {
  if (!React.isValidElement(children) || children.type !== "table") {
    return null;
  }

  const { className, children: tableChildren, ...tableProps } = children.props;
  const parts = React.Children.toArray(tableChildren);
  const thead = parts.find(
    (child) => React.isValidElement(child) && child.type === "thead"
  );
  const body = parts.filter(
    (child) => !(React.isValidElement(child) && child.type === "thead")
  );

  return { className, tableProps, thead, body };
};

const StickyTable = ({
  children,
  refreshDeps = [],
  scrollClassName = "text-nowrap",
  wrapClassName = "",
}) => {
  const parsedTable = parseTableElement(children);
  const {
    tableWrapRef,
    tableScrollRef,
    tableRef,
    theadRef,
    fixedHeaderTableRef,
    fixedHeaderWrapRef,
    tableScrollLeft,
    isHeaderFixed,
    fixedHeaderMetrics,
    handleTableScroll,
  } = useStickyTableHeader(refreshDeps);

  const scrollClasses = ["table-responsive", "sticky-table-body-scroll", scrollClassName]
    .filter(Boolean)
    .join(" ");
  const wrapClasses = ["sticky-table-wrap", wrapClassName].filter(Boolean).join(" ");

  if (!parsedTable) {
    return children;
  }

  const { className, tableProps, thead, body } = parsedTable;
  const theadProps = thead?.props || {};
  const hasAction = checkHasActionColumn(thead);
  const resolvedClassName = [
    className,
    hasAction ? "table-freeze-action" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div ref={tableWrapRef} className={wrapClasses}>
      {isHeaderFixed && (
        <div
          ref={fixedHeaderWrapRef}
          className="sticky-table-fixed-header"
          style={{
            position: "fixed",
            top: fixedHeaderMetrics.top,
            left: fixedHeaderMetrics.left,
            width: fixedHeaderMetrics.width,
            zIndex: 100,
            overflow: "hidden",
          }}
        >
          <table
            ref={fixedHeaderTableRef}
            className={resolvedClassName}
            style={{
              width: fixedHeaderMetrics.tableWidth,
              minWidth: fixedHeaderMetrics.tableWidth,
            }}
            {...tableProps}
          >
            <thead {...theadProps}>{theadProps.children}</thead>
          </table>
        </div>
      )}
      <div
        ref={tableScrollRef}
        className={scrollClasses}
        onScroll={handleTableScroll}
      >
        <table ref={tableRef} className={resolvedClassName} {...tableProps}>
          <thead ref={theadRef} {...theadProps}>
            {theadProps.children}
          </thead>
          {body}
        </table>
      </div>
    </div>
  );
};

export default StickyTable;
